/**
 * Scenario freeze + capital-pack export (D3).
 * Desk knobs → immutable snapshot with content hash; exports for IC / LP.
 */
import { summarizeCalibration } from './calibration.js';
import { evaluateReleaseReadiness, normalizePostCloseActuals } from './releaseGate.js';
import {
  ILLUSTRATIVE_BUYER_OH_MONTHLY,
  pickBuyerOverheadSnapshot,
  redactIllustrativeOverhead,
  scrubIllustrativeDollars,
} from './buyerOverhead.js';
import { pickLiabilitySnapshot } from './liabilitySchedule.js';
import { bidProcessFromMeta, pickProposalSnapshot } from './bidProposal.js';
import { pickForecastFreezeRef } from '../assets/forecastFreezeRef.js';

function sortKeys(value) {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, k) => {
        acc[k] = sortKeys(value[k]);
        return acc;
      }, {});
  }
  return value;
}

export function stableStringify(value) {
  return JSON.stringify(sortKeys(value));
}

/** FNV-1a 32-bit → 8-char hex for local desk saves only; never an approval authority. */
export function contentHash(value) {
  const s = typeof value === 'string' ? value : stableStringify(value);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

function pickResult(r) {
  if (!r) return null;
  return {
    pvLabel: r.pvLabel,
    pvMM: +r.pvMM.toFixed(3),
    bidBridgePvMM: +r.bidBridgePvMM.toFixed(3),
    ntmCfMM: +r.ntmCfMM.toFixed(3),
    undiscCfMM: +r.undiscCfMM.toFixed(3),
    boswellShare: +r.boswellShare.toFixed(4),
    irrAnnual: r.irrAnnual == null ? null : +r.irrAnnual.toFixed(4),
    moic: r.moic == null ? null : +r.moic.toFixed(3),
    multiples: {
      x4: +r.multiples.x4.toFixed(2),
      x5: +r.multiples.x5.toFixed(2),
      x6: +r.multiples.x6.toFixed(2),
    },
    irrWithoutBuyerOh: r.irrWithoutBuyerOh == null ? null : +r.irrWithoutBuyerOh.toFixed(4),
    pvWithoutBuyerOhMM: r.pvWithoutBuyerOhMM == null ? null : +r.pvWithoutBuyerOhMM.toFixed(3),
    buyerOhPvDeltaMM: r.buyerOhPvDeltaMM == null ? null : +r.buyerOhPvDeltaMM.toFixed(3),
  };
}

/**
 * Build an immutable freeze from live desk state.
 * `contentHash` is a local convenience ID. External release requires the
 * server-generated SHA-256 digest on an approved capital case.
 */
/** Compact, hashable Boswell DCA / production-diagnostic snapshot for the pack. */
function pickBoswellDca(dca, assumptions) {
  if (!dca) return null;
  const d = dca.diagnostics || {};
  return {
    troughMcfpd: dca.troughMcfpd ?? null,
    postAcidMcfpd: dca.postAcidMcfpd ?? null,
    fitSource: dca.fitSource ?? null,
    fittedDePct: dca.DeUsedPct ?? null,
    hyperbolicB: dca.hyperbolic?.b ?? null,
    hyperbolicR2: dca.hyperbolic?.r2 ?? null,
    holdoutMapePct: dca.holdoutMapePct ?? null,
    fadeMonths: dca.fadeMonths ?? null,
    fadeSource: dca.fadeSource ?? null,
    fitReady: dca.fitReady ?? null,
    // The single volume judgment this scenario locked in.
    volAdj: assumptions?.boswellVolAdj ?? null,
    uptimeFraction: d.uptime?.flowingFraction ?? null,
    wgrBblPerMmcf: d.wgr?.wgrBblPerMmcf ?? null,
    avgFtp: d.ftp?.avgFtp ?? null,
    shortWindow: dca.shortWindow ?? null,
  };
}

export function buildScenarioFreeze({
  pkg,
  assumptions,
  label = 'Working case',
  note = '',
  base,
  stress,
  scenarioRows = [],
  approver = '',
  recommendation = '',
  midstream = null,
  dca = null,
  releaseReadiness = null,
  postCloseActuals = null,
  proposal = null,
  absRunRef = null,
  forecastFreezeRef = null,
}) {
  const cal = summarizeCalibration(pkg);
  const release = evaluateReleaseReadiness(releaseReadiness, {
    asOf: pkg?.meta?.generatedAt || pkg?.meta?.priceDeck || '',
  });
  const bidProcess = bidProcessFromMeta(pkg?.meta || {});
  const proposalSnap = pickProposalSnapshot(
    proposal || {
      purchasePriceMM: assumptions?.bidPriceMM,
      letterheadEntity: pkg?.meta?.caParty,
      effectiveDate: bidProcess.effectiveDate,
      validThrough: bidProcess.proposalValidThrough,
    },
  );
  const core = {
    schemaVersion: 'freeze.v3',
    dealId: pkg?.meta?.dealId || 'eagle-etx-2026',
    generatorVersion: pkg?.meta?.generatorVersion || pkg?.provenance?.generatorVersion || null,
    asOf: pkg?.meta?.generatedAt || pkg?.meta?.priceDeck || null,
    label,
    bidProcess: {
      bidsDue: bidProcess.bidsDue,
      bidDueLabel: bidProcess.bidDueLabel,
      effectiveDate: bidProcess.effectiveDate,
      proposalValidThrough: bidProcess.proposalValidThrough,
      submitTo: bidProcess.submitTo,
      cc: bidProcess.cc,
      packageName: bidProcess.packageName,
      formatNote: bidProcess.formatNote,
    },
    proposal: proposalSnap,
    assumptions: {
      hh: assumptions.hh,
      wti: assumptions.wti ?? null,
      hhBase: assumptions.hhBase ?? null,
      wtiBase: assumptions.wtiBase ?? null,
      primaryProduct: assumptions.primaryProduct ?? pkg?.meta?.primaryProduct ?? null,
      multiProduct: !!assumptions.multiProduct,
      products: pkg?.meta?.products || null,
      boswellVolAdj: assumptions.boswellVolAdj,
      netbackAdj: assumptions.netbackAdj,
      discountRate: assumptions.discountRate,
      aroMM: assumptions.aroMM,
      overheadInCf: !!assumptions.overheadInCf,
      overheadMonthly: assumptions.overheadMonthly,
      buyerOverhead: pickBuyerOverheadSnapshot(assumptions),
      bidPriceMM: assumptions.bidPriceMM,
    },
    liabilitySchedule: pickLiabilitySnapshot(base?.liabilitySchedule),
    results: {
      base: pickResult(base),
      stress: pickResult(stress),
      namedScenarios: scenarioRows.map((s) => ({
        id: s.id,
        label: s.scenario,
        pvMM: s.pv,
        bridgeMM: s.bridge,
        ntmCfMM: s.ntm,
        irrAnnual: s.irr,
        x5MM: s.x5,
      })),
    },
    seller: {
      askMM: 13,
      refPdpPv10MM: pkg?.validation?.refPdpPv10MM ?? null,
      refTotalPv10MM: pkg?.validation?.refTotalPv10MM ?? null,
    },
    calibration: cal
      ? {
          modelBasePv10MM: cal.modelBasePv10MM,
          refPdpPv10MM: cal.refPdpPv10MM,
          modelVsRefPdpMM: cal.modelVsRefPdpMM,
          cfScaleMedian: cal.cfScale.median,
          cfScaleBoswell: cal.cfScale.boswell,
          cfScaleOutliers: cal.cfScale.outlierCount,
        }
      : null,
    midstream: midstream
      ? {
          overallStatus: midstream.overallStatus,
          openCount: midstream.openCount ?? midstream.contracts?.filter((c) => c.status === 'Open').length,
          contractCount: midstream.contractCount ?? midstream.contracts?.length,
          l12Realized: midstream.l12Realized,
          stackAt6k: midstream.stackAt6k,
        }
      : null,
    boswellDca: pickBoswellDca(dca, assumptions),
    recommendation: recommendation || '',
    releaseReadiness: release.readiness,
    // Reserve a stable, path-safe seam for post-close actual-versus-underwritten
    // evidence without treating VDR historical actuals as sponsor servicing history.
    postCloseActuals: normalizePostCloseActuals(postCloseActuals),
    // ABS handshake ref only (SPEC-221e) — never ABS numeric results.
    ...(absRunRef && typeof absRunRef === 'object' ? { absRunRef } : {}),
    // Approved per-well forecast set (asset workflow 1.9) — id + SHA-256 only.
    ...(pickForecastFreezeRef(forecastFreezeRef) ? { forecastFreezeRef: pickForecastFreezeRef(forecastFreezeRef) } : {}),
  };

  const hash = contentHash(core);
  const frozenAt = new Date().toISOString();
  const short = frozenAt.slice(0, 10).replace(/-/g, '');
  return {
    ...core,
    scenarioId: `${core.dealId}-${short}-${hash}`,
    contentHash: hash,
    frozenAt,
    note: note || '',
    approver: approver || '',
    confidentiality: pkg?.meta?.confidentiality || pkg?.meta?.dataHandling || 'Firebrand Energy / ETXENERGY CA',
  };
}

export function downloadJson(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadText(filename, text, mime = 'text/plain') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function usd(v) {
  return `$${Number(v || 0).toLocaleString()}`;
}

/** Null monthly is the redacted illustrative figure, not a $0 overhead. */
function overheadCfLabel(assumptions) {
  const inCf = assumptions?.overheadInCf ? 'Yes' : 'No';
  const monthly = assumptions?.overheadMonthly;
  const amount = monthly == null ? 'illustrative, omitted' : `${usd(monthly)}/mo`;
  return `${inCf} (${amount})`;
}

/** Buyer overhead categories markdown section — desk judgment, not seller fact. */
function buyerOverheadMarkdown(freeze) {
  const oh = freeze.assumptions?.buyerOverhead;
  const b = freeze.results?.base;
  if (!oh) return '_No buyer overhead snapshot captured in this freeze._';
  const rows = (oh.categories || [])
    .map((c) => `| ${c.label} | ${c.redacted || c.monthly == null ? 'illustrative, omitted' : `${usd(c.monthly)}/mo`} | ${c.includeInCf ? 'Yes' : 'No'} |`)
    .join('\n');
  return `${oh.illustrationNote || 'Buyer G&A categories are desk judgment, not seller facts.'}

| Category | $/mo | In CF |
| --- | --- | --- |
${rows}

**Total:** ${oh.monthlyTotal == null ? 'illustrative, omitted' : `${usd(oh.monthlyTotal)}/mo`} · **Included in CF:** ${oh.monthlyIncluded == null ? 'illustrative, omitted' : `${usd(oh.monthlyIncluded)}/mo`} · **PV impact:** ${
    b?.buyerOhPvDeltaMM != null ? `$${b.buyerOhPvDeltaMM.toFixed(2)}MM` : '—'
  } (IRR w/o buyer OH ${pct(b?.irrWithoutBuyerOh)})`;
}

/** Liability schedule markdown section — negative-PV / inactive wells, never floored. */
function liabilityScheduleMarkdown(freeze) {
  const l = freeze.liabilitySchedule;
  if (!l || !l.rowCount) return '_No negative-PV / inactive wells flagged in this freeze._';
  const rows = l.rows
    .map(
      (r) =>
        `| ${r.lease} | ${usd(r.onelinePv)} | ${r.modelAssetPv != null ? usd(r.modelAssetPv) : '—'} | ${
          r.suggestedPlugAroShareMM != null ? `$${r.suggestedPlugAroShareMM.toFixed(3)}MM` : '—'
        } | ${r.regulatoryNote || ''} |`,
    )
    .join('\n');
  return `${l.rowCount} well(s) carrying negative oneline PV / inactive flags — retained in package totals (not floored to zero).

| Lease | Oneline PV | Model asset PV | Plug/ARO share | Note |
| --- | --- | --- | --- | --- |
${rows}

Oneline negative total: ${usd(l.onelineNegativeTotal)} · Model negative total: ${usd(l.modelNegativeTotal)}`;
}

function externalOverheadFreeze(freeze) {
  if (!freeze?.assumptions) return freeze;
  const monthly = freeze.assumptions.overheadMonthly;
  const illustrative = Math.abs(Number(monthly) - ILLUSTRATIVE_BUYER_OH_MONTHLY) < 1;
  return {
    ...freeze,
    assumptions: {
      ...freeze.assumptions,
      buyerOverhead: redactIllustrativeOverhead(freeze.assumptions.buyerOverhead),
      overheadMonthly: illustrative ? null : monthly,
    },
    disclosures: Array.isArray(freeze.disclosures)
      ? freeze.disclosures.map((d) => scrubIllustrativeDollars(d))
      : freeze.disclosures,
  };
}

/** Buyer-facing one-pager markdown from a freeze. The illustrative G&A figure is omitted. */
export function capitalOnePagerMarkdown(freezeIn) {
  const freeze = externalOverheadFreeze(freezeIn);
  const a = freeze.assumptions;
  const b = freeze.results.base;
  const s = freeze.results.stress;
  return `# ${freeze.label || 'Capital one-pager'} (frozen)

**Confidential:** ${freeze.confidentiality}
**Scenario ID:** \`${freeze.scenarioId}\`
**Content hash:** \`${freeze.contentHash}\`
**Frozen at:** ${freeze.frozenAt}
**Label:** ${freeze.label}
**DealPackage:** ${freeze.generatorVersion || '—'} · as-of ${freeze.asOf || '—'}

---

## Concentration (disclose first)

Largest well is **${b ? (b.boswellShare * 100).toFixed(0) : '—'}%** model-basis share of modeled PDP PV. Treat concentration as single-well risk, not diversified PDP.

## Bid bridge (locked)

| Item | Value | Class |
| --- | --- | --- |
| Bid price | $${a.bidPriceMM.toFixed(2)}MM | Judgment |
| Gross ${b?.pvLabel || 'PV'} | $${b?.pvMM.toFixed(2)}MM | Model |
| ARO / P&A | $${a.aroMM.toFixed(2)}MM | Judgment |
| After ARO | $${b?.bidBridgePvMM.toFixed(2)}MM | Model |
| NTM CF | $${b?.ntmCfMM.toFixed(2)}MM | Model |
| IRR @ bid (base) | ${pct(b?.irrAnnual)} | Computed |
| MOIC @ bid (before P&A) | ${b?.moic != null ? `${b.moic.toFixed(2)}×` : '—'} | Computed |
| IRR @ bid (desk stress) | ${pct(s?.irrAnnual)} | Computed |

**Desk stress (locked):** oil −$5/bbl and netback −$2.50/bbl, or gas −$0.50/MMBtu and netback −$0.15/Mcf; largest well −25%; buyer G&A in cash flow. Not the packaged named \`bear\` scenario.

## Locked assumptions

| Knob | Value |
| --- | --- |
| HH | $${a.hh.toFixed(2)} |
| Boswell vol adj | ${(a.boswellVolAdj * 100).toFixed(0)}% |
| Netback adj | $${a.netbackAdj.toFixed(2)}/Mcf |
| Discount | ${(a.discountRate * 100).toFixed(0)}% |
| Overhead in CF | ${overheadCfLabel(a)} |

## Buyer overhead (categories · judgment)

${buyerOverheadMarkdown(freeze)}

## Liability schedule (negative PV / inactive — not floored)

${liabilityScheduleMarkdown(freeze)}

## Seller reference (fact)

- Seller ask framing: ~$${freeze.seller.askMM}MM (working)
- Oneline PDP PV10: $${freeze.seller.refPdpPv10MM ?? '—'}MM
- Oneline total PV10: $${freeze.seller.refTotalPv10MM ?? '—'}MM

## Multiple back-check only (not the bid frame)

4× $${b?.multiples.x4.toFixed(1)}MM · 5× $${b?.multiples.x5.toFixed(1)}MM · 6× $${b?.multiples.x6.toFixed(1)}MM on NTM CF.

## Named scenarios at bid

${(freeze.results.namedScenarios || [])
  .map(
    (r) =>
      `- **${r.label}** — PV $${r.pvMM.toFixed(2)}MM · after ARO $${r.bridgeMM.toFixed(2)}MM · IRR ${pct(r.irrAnnual)}`,
  )
  .join('\n')}

## Note

${freeze.note || '_None_'}

## Approver

${freeze.approver || '_Pending_'}

---

*Generated from the Eagle DealPackage client engine. Editable knobs are for the desk; circulate this freeze only.*
`;
}

/** Internal IC decision memo (markdown) — fuller than the LP one-pager. */
export function capitalIcMemoMarkdown(freeze) {
  const a = freeze.assumptions;
  const b = freeze.results.base;
  const s = freeze.results.stress;
  const cal = freeze.calibration;
  const ms = freeze.midstream;
  const scenarios = freeze.results.namedScenarios || [];

  return `# ${freeze.label || 'Working case'} — IC decision memo (frozen)

**Confidential:** ${freeze.confidentiality}
**Scenario ID:** \`${freeze.scenarioId}\`
**Content hash:** \`${freeze.contentHash}\`
**Frozen at:** ${freeze.frozenAt}
**Label:** ${freeze.label}
**DealPackage:** ${freeze.generatorVersion || '—'} · as-of ${freeze.asOf || '—'}

---

## Working recommendation (judgment)

${freeze.recommendation || '_Pending IC — set on Decision desk before freeze._'}

**Note:** ${freeze.note || '_None_'}
**Approver:** ${freeze.approver || '_Pending_'}

---

## Concentration (disclose first)

Largest well is **${b ? (b.boswellShare * 100).toFixed(0) : '—'}%** model-basis share of modeled PDP PV.

| Item | Value | Class |
| --- | --- | --- |
| Seller ask (working) | $${freeze.seller.askMM}MM | Fact / framing |
| Oneline PDP PV10 | $${freeze.seller.refPdpPv10MM ?? '—'}MM | Fact |
| Model ${b?.pvLabel || 'PV'} (base) | $${b?.pvMM.toFixed(2)}MM | Model |
| After ARO | $${b?.bidBridgePvMM.toFixed(2)}MM | Model |
| Bid price | $${a.bidPriceMM.toFixed(2)}MM | Judgment |
| IRR @ bid (base) | ${pct(b?.irrAnnual)} | Computed |
| IRR @ bid (desk stress) | ${pct(s?.irrAnnual)} | Computed |
| MOIC @ bid (before P&A) | ${b?.moic != null ? `${b.moic.toFixed(2)}×` : '—'} | Computed |
| NTM CF | $${b?.ntmCfMM.toFixed(2)}MM | Model |

**Desk stress (locked with this freeze):** oil −$5/bbl and netback −$2.50/bbl, or gas −$0.50/MMBtu and netback −$0.15/Mcf; largest well −25%; buyer G&A in cash flow. This is the Decision-desk stress run, **not** the packaged named \`bear\` scenario (which uses its own delta set and may differ).

## Locked assumptions

| Knob | Value |
| --- | --- |
| HH | $${a.hh.toFixed(2)} |
| Boswell vol adj | ${(a.boswellVolAdj * 100).toFixed(0)}% |
| Netback adj | $${a.netbackAdj.toFixed(2)}/Mcf |
| Discount | ${(a.discountRate * 100).toFixed(0)}% |
| ARO / P&A | $${a.aroMM.toFixed(2)}MM |
| Overhead in CF | ${a.overheadInCf ? 'Yes' : 'No'} (${usd(a.overheadMonthly)}/mo) |

## Buyer overhead (categories · judgment)

${buyerOverheadMarkdown(freeze)}

## Liability schedule (negative PV / inactive — not floored)

${liabilityScheduleMarkdown(freeze)}

## Named scenarios at bid

Packaged DealPackage deltas re-priced at the locked bid (ARO / overhead from desk). These are **separate** from desk stress above — do not treat \`bear\` IRR as a restatement of desk-stress IRR.

${scenarios
  .map(
    (r) =>
      `- **${r.label}** (\`${r.id}\`) — PV $${r.pvMM.toFixed(2)}MM · after ARO $${r.bridgeMM.toFixed(2)}MM · IRR ${pct(r.irrAnnual)}`,
  )
  .join('\n')}

## Model fidelity (cfScale)

Streams are decline-reconstructed from ARIES, then each asset’s monthly CF is scaled (\`cfScale\`) so NPV10 @ base realized ≈ seller oneline PV10.

| Check | Value |
| --- | --- |
| Model base PV10 | $${cal?.modelBasePv10MM ?? '—'}MM |
| Seller PDP PV10 | $${cal?.refPdpPv10MM ?? '—'}MM |
| Model − seller | $${cal?.modelVsRefPdpMM ?? '—'}MM |
| cfScale median | ${cal?.cfScaleMedian ?? '—'} |
| cfScale Boswell | ${cal?.cfScaleBoswell ?? '—'} |
| Outliers (|scale−1| > 0.3) | ${cal?.cfScaleOutliers ?? '—'} |

## Boswell production / DCA (fact vs judgment)

${
  freeze.boswellDca
    ? `Trough **${freeze.boswellDca.troughMcfpd ?? '—'}** Mcf/d · post-acid **${freeze.boswellDca.postAcidMcfpd ?? '—'}** Mcf/d · fitted De **${freeze.boswellDca.fittedDePct ?? '—'}%/yr** (${freeze.boswellDca.fitSource || '—'})${freeze.boswellDca.hyperbolicB != null ? ` · hyperbolic b ${freeze.boswellDca.hyperbolicB} (R² ${freeze.boswellDca.hyperbolicR2 ?? '—'})` : ''}${freeze.boswellDca.holdoutMapePct != null ? ` · holdout MAPE ${freeze.boswellDca.holdoutMapePct}%` : ''}. Locked \`boswellVolAdj\` **${freeze.boswellDca.volAdj != null ? (freeze.boswellDca.volAdj * 100).toFixed(0) : '—'}%** — the ONLY volume judgment (Fact = actuals; Judgment = the dial). Fit-ready: **${freeze.boswellDca.fitReady == null ? '—' : freeze.boswellDca.fitReady ? 'yes' : 'NO'}**${freeze.boswellDca.shortWindow ? ' · ⚠ post-acid window < 90d cannot alone prove durability' : ''}.`
    : '_No DCA snapshot captured in this freeze — open Sensitivity to attach one._'
}

## Midstream assignability (diligence)

${
  ms
    ? `**Status: ${(ms.overallStatus || 'unknown').toUpperCase()}** · ${ms.openCount ?? '—'}/${ms.contractCount ?? '—'} contracts open · L12M realized $${ms.l12Realized ?? '—'}/Mcf · stack ~$${ms.stackAt6k ?? '—'} @ 6k MMBtu/d.`
    : '_Not captured in this freeze — see Sensitivity / Diligence register._'
}

## Multiple back-check only (not the bid frame)

4× $${b?.multiples.x4.toFixed(1)}MM · 5× $${b?.multiples.x5.toFixed(1)}MM · 6× $${b?.multiples.x6.toFixed(1)}MM on NTM CF.

## Circulation rule

Editable knobs stay on the desk. Circulate this memo + matching JSON freeze (\`${freeze.contentHash}\`) only. Companion exports: one-pager (.md), underwriting (.xlsx), scenario register (.csv).

---

*Eagle DealPackage client engine · capital pack D3*
`;
}

export function scenarioRegisterCsv(freezes) {
  const header = [
    'scenarioId',
    'contentHash',
    'frozenAt',
    'label',
    'bidPriceMM',
    'hh',
    'boswellVolAdj',
    'netbackAdj',
    'discountRate',
    'aroMM',
    'overheadInCf',
    'basePvMM',
    'baseIrr',
    'stressIrr',
    'boswellShare',
    'approver',
  ];
  const rows = freezes.map((f) =>
    [
      f.scenarioId,
      f.contentHash,
      f.frozenAt,
      JSON.stringify(f.label),
      f.assumptions.bidPriceMM,
      f.assumptions.hh,
      f.assumptions.boswellVolAdj,
      f.assumptions.netbackAdj,
      f.assumptions.discountRate,
      f.assumptions.aroMM,
      f.assumptions.overheadInCf,
      f.results.base?.pvMM,
      f.results.base?.irrAnnual,
      f.results.stress?.irrAnnual,
      f.results.base?.boswellShare,
      JSON.stringify(f.approver || ''),
    ].join(','),
  );
  return [header.join(','), ...rows].join('\n');
}

const REGISTER_KEY = 'eagle-scenario-register-v1';

export function loadRegister() {
  try {
    const raw = localStorage.getItem(REGISTER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveRegister(freezes) {
  localStorage.setItem(REGISTER_KEY, JSON.stringify(freezes.slice(0, 50)));
}

export function appendRegister(freeze) {
  const next = [freeze, ...loadRegister().filter((f) => f.contentHash !== freeze.contentHash)];
  saveRegister(next);
  return next;
}

function normalizeLabel(label) {
  return String(label || '')
    .trim()
    .toLowerCase();
}

/** Save (or overwrite same name) a named scenario in the local register. */
export function upsertNamedScenario(freeze) {
  const key = normalizeLabel(freeze.label);
  if (!key) throw new Error('Scenario name is required');
  const frozen = { ...freeze, frozenAt: freeze.frozenAt || new Date().toISOString() };
  const rest = loadRegister().filter(
    (f) => normalizeLabel(f.label) !== key && f.contentHash !== frozen.contentHash,
  );
  const next = [frozen, ...rest];
  saveRegister(next);
  return next;
}

export function removeNamedScenario(scenarioId) {
  const next = loadRegister().filter((f) => f.scenarioId !== scenarioId);
  saveRegister(next);
  return next;
}

/** Apply a saved scenario’s knobs back onto the Decision desk. */
export function deskStateFromScenario(freeze) {
  const a = freeze?.assumptions || {};
  return {
    bidPriceMM: a.bidPriceMM,
    hh: a.hh,
    boswellVolAdj: a.boswellVolAdj,
    netbackAdj: a.netbackAdj,
    discountRate: a.discountRate,
    aroMM: a.aroMM,
    overheadInCf: !!a.overheadInCf,
    overheadMonthly: a.overheadMonthly,
    buyerOverheadCategories: a.buyerOverhead?.categories || [],
    freezeLabel: freeze.label || 'Working base',
    freezeNote: freeze.note || '',
    freezeApprover: freeze.approver || '',
    proposal: freeze.proposal || null,
  };
}

function deltaOf(x, y) {
  if (x == null || y == null) return null;
  return +(y - x).toFixed(4);
}

/** Compare two saved freezes: PV / IRR / buyer-OH / liability / bid-bridge deltas (right − left). */
export function compareFreezes(a, b) {
  if (!a || !b) return null;
  const ab = a.results?.base;
  const bb = b.results?.base;
  const aOh = a.assumptions?.buyerOverhead;
  const bOh = b.assumptions?.buyerOverhead;
  const aLiab = a.liabilitySchedule;
  const bLiab = b.liabilitySchedule;
  return {
    left: { scenarioId: a.scenarioId, label: a.label },
    right: { scenarioId: b.scenarioId, label: b.label },
    pvMM: { left: ab?.pvMM ?? null, right: bb?.pvMM ?? null, delta: deltaOf(ab?.pvMM, bb?.pvMM) },
    irrAnnual: {
      left: ab?.irrAnnual ?? null,
      right: bb?.irrAnnual ?? null,
      delta: deltaOf(ab?.irrAnnual, bb?.irrAnnual),
    },
    buyerOhMonthlyIncluded: {
      left: aOh?.monthlyIncluded ?? null,
      right: bOh?.monthlyIncluded ?? null,
      delta: deltaOf(aOh?.monthlyIncluded, bOh?.monthlyIncluded),
    },
    liabilityOnelineTotal: {
      left: aLiab?.onelineNegativeTotal ?? null,
      right: bLiab?.onelineNegativeTotal ?? null,
      delta: deltaOf(aLiab?.onelineNegativeTotal, bLiab?.onelineNegativeTotal),
    },
    bidBridgePvMM: {
      left: ab?.bidBridgePvMM ?? null,
      right: bb?.bidBridgePvMM ?? null,
      delta: deltaOf(ab?.bidBridgePvMM, bb?.bidBridgePvMM),
    },
  };
}
